import type { ComponentType, FunctionComponent, ReactNode } from 'react'
import type { UseMutationProps } from '@ztechapps/common.hook'
import { useFeatureFlag } from '@ztechapps/common.hook'
import type { InlineInputUpdateParamsFunction } from '@ztechapps/webui.input'
import type { Address } from '@ztechapps/common.contract'
import { ComplexEditorLayoutChoiceEnum, FeatureFlagKeyEnum } from '@ztechapps/common.contract'
import { FeatureFlagLoading } from '@ztechapps/webui.data-tools'
import type { AddressSelectEditContentProps } from '../Address/AddressSelectEditContent'
import { useAddressEditPopOverContext } from './Context'
import { AddressInlineEditField } from './AddressInlineEditField'
import { AddressPopoverEditField } from './AddressPopoverEditField'

export type AddressEditFieldProps = {
  /** The surface's unique open-state key, matched against the shared Context's `openKey`. Named
   * `editorKey` rather than `key` because `key` is a reserved React prop and cannot be read back
   * as a normal prop value. */
  editorKey: string
  name: string
  label?: ReactNode
  initialValues?: Record<string, any>
  /** The address to RENDER read-only, when it must differ from the stored value the editor
   * edits — e.g. a logistic block showing its contact's phone while the address stores none.
   * Defaults to the stored value. It never reaches the editor or a mutation. */
  displayAddress?: Address
  mutationConfig: UseMutationProps
  updateParams?: InlineInputUpdateParamsFunction
  addressSelectComponent: ComponentType<any>
  addressSelectProps?: AddressSelectEditContentProps['addressSelectProps']
  extraActions?: AddressSelectEditContentProps['extraActions']
  buildFilter?: AddressSelectEditContentProps['buildFilter']
  parseCreateOption?: AddressSelectEditContentProps['parseCreateOption']
  emptyContent?: ReactNode
  /** Undefined behaves as `true` (editable), matching `InlineInput`'s own default. Left
   * unresolved (never defaulted to a boolean) so the INLINE arm can still forward it to
   * `parentInputComponent`, which may default it itself from document context. */
  canEdit?: boolean
  /** Named why `canEdit` is `false` (e.g. "This order has been delivered."), surfaced on the
   * disabled address's title in place of the generic "Editing is disabled". Only the POPOVER arm
   * acts on it — the INLINE arm's `canEdit === false` title is `InlineInput`'s own hardcoded
   * string (libs/webui/input/src/InlineInput/index.tsx), out of scope to change here. */
  disabledReason?: string
  parentInputComponent?: FunctionComponent
}

// The single component all six address surfaces render. It is the ONE place the
// `COMPLEX_EDITOR_LAYOUT` flag is branched on — per-surface pickers, mutations and
// AI-wand actions all travel through these props into whichever arm the flag selects. Each arm
// owns only its own hooks (shipped precedent: apps/panelpunches/admin/src/view/contacts/list/index.tsx).
export const AddressEditField = (props: AddressEditFieldProps) => {
  const { hasProvider } = useAddressEditPopOverContext()

  // No `AddressEditPopOverProvider` mounted above this surface — e.g. the storefront's
  // `OrderInlineInput` tree, which never wires one. The POPOVER arm's single-open-key exclusivity
  // has nothing to coordinate with there, so render the self-contained INLINE arm regardless of
  // the tenant-wide flag. `useFeatureFlag` (a real `useList` network round trip) is read only in
  // `AddressEditFieldFlagged` below, which is never mounted on this branch — the storefront must
  // never issue that request when it can never act on the answer.
  if (!hasProvider) {
    return <AddressInlineEditField {...props} />
  }

  return <AddressEditFieldFlagged {...props} />
}

const AddressEditFieldFlagged = (props: AddressEditFieldProps) => {
  const { selectedChoice, loading } = useFeatureFlag<ComplexEditorLayoutChoiceEnum>(
    FeatureFlagKeyEnum.COMPLEX_EDITOR_LAYOUT
  )

  if (loading) {
    return <FeatureFlagLoading featureName={FeatureFlagKeyEnum.COMPLEX_EDITOR_LAYOUT} />
  }

  if (selectedChoice === ComplexEditorLayoutChoiceEnum.INLINE) {
    return <AddressInlineEditField {...props} />
  }

  // A stored value that is no longer a member of the choice falls through to the same arm as
  // the shipped default (POPOVER).
  return <AddressPopoverEditField {...props} />
}
